import { useCallback, useState } from "react";
import { QueryKey, useQueryClient } from "@tanstack/react-query";

import { MessageRow, MessagesWithPagination } from "../../api/message.type";

export type SyncMessagesFn = (
  compute: (
    prev: MessageRow[]
  ) => { next: MessageRow[]; totalCountDelta?: number }
) => void;

interface UseMessageSyncOptions {
  queryKey: QueryKey;
  queryEnabled: boolean;
}

export function useMessageSync({
  queryKey,
  queryEnabled,
}: UseMessageSyncOptions) {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const syncMessages: SyncMessagesFn = useCallback(
    (compute) => {
      let nextMessages: MessageRow[] = [];
      let deltaValue = 0;

      setMessages((prev) => {
        const { next, totalCountDelta = 0 } = compute(prev);
        nextMessages = next;
        deltaValue = totalCountDelta;

        if (queryEnabled) {
          queryClient.setQueryData<MessagesWithPagination>(queryKey, (cached) => {
            if (!cached) {
              return {
                messages: next,
                totalCount: next.length,
                nextPage: null,
              } satisfies MessagesWithPagination;
            }

            return {
              ...cached,
              messages: next,
              totalCount: Math.max(cached.totalCount + totalCountDelta, 0),
            };
          });
        }

        return next;
      });

      setTotalCount((prev) => {
        if (!Number.isFinite(deltaValue) || deltaValue === 0) {
          return prev;
        }
        if (prev === null) {
          return Math.max(nextMessages.length, 0);
        }
        return Math.max(prev + deltaValue, 0);
      });
    },
    [queryClient, queryEnabled, queryKey]
  );

  const resetMessages = useCallback(() => {
    setMessages([]);
    setTotalCount(null);
  }, []);

  return {
    messages,
    setMessages,
    totalCount,
    setTotalCount,
    syncMessages,
    resetMessages,
  } as const;
}
